// The `.mc` island App.tsx hosts a registered (V2) view in, and the V1 slot beside it (plan D12, the scoping rule: V1
// never renders inside `.mc`). V1 and V2 share token names, so a V1 control under a `.mc` element takes V2's colours
// against V1's own backgrounds (a V1 Resume button read 1.66:1 on the HM's held requisition in the light theme).
//
// A V2 screen that still carries V1 parts (the not-found screen, a record read's refusal or loading boundary, the V1
// requisition controls with no word of his yet, the Interviewer's V1 requisition page) wraps each in <V1Slot>. Inside an
// island the slot portals its children into a host placed just above the island, outside every `.mc` element, so they
// keep V1's look while React context (session, route, design-system root) still reaches them. Outside an island, and
// inside a slot already, it renders its children in place, so V1 screens are unchanged.

import { createContext, useContext, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

/** undefined: not inside an island (render in place); null: the island's host is not mounted yet; else the host. */
const SlotHost = createContext<HTMLElement | null | undefined>(undefined);

/** A registered view's island: the V1 slot's host first, then the `.mc` box the view renders in. */
export function Island({ children }: { children: ReactNode }) {
  const [host, setHost] = useState<HTMLElement | null>(null);
  return (
    <>
      <div ref={setHost} data-v1-slot="" />
      <SlotHost.Provider value={host}>
        <div className="mc">{children}</div>
      </SlotHost.Provider>
    </>
  );
}

/** V1 content a V2 screen still draws: outside the island's `.mc`, in the order the slots mount. */
export function V1Slot({ children }: { children: ReactNode }) {
  const host = useContext(SlotHost);
  if (host === undefined) return <>{children}</>;
  if (host === null) return null;
  return createPortal(<SlotHost.Provider value={undefined}>{children}</SlotHost.Provider>, host);
}
